import axios from 'axios';
import { API_URL } from '../config/api';
import SessionService from './SessionService';

export interface EmailConnectionStatus {
  sectorId: number;
  provider: string | null;
  status: string;
  lastConnectedAt: string | null;
  lastError: string | null;
  hasConnection: boolean;
}

const getAuthHeaders = () => {
  const token = SessionService.getToken();
  if (!token) throw new Error('Authentication token not found');
  return { Authorization: `Bearer ${token}` };
};

export const getEmailConnectionStatus = async (sectorId: number): Promise<EmailConnectionStatus> => {
  const response = await axios.get<EmailConnectionStatus>(`${API_URL}/EmailConnection/status/${sectorId}`, {
    headers: getAuthHeaders(),
  });
  return response.data;
};

export const getOAuthAuthorizeUrl = (sectorId: number): string =>
  `${API_URL}/EmailConnection/oauth/authorize?sectorId=${sectorId}`;

export const fetchOAuthAuthorizeUrlAndRedirect = async (sectorId: number): Promise<void> => {
  const response = await axios.get<{ url: string }>(
    `${API_URL}/EmailConnection/oauth/authorize-url?sectorId=${sectorId}`,
    { headers: getAuthHeaders() }
  );
  const url = response.data?.url;
  if (!url) throw new Error('No redirect URL received');
  window.location.href = url;
};

/** Outlook / Microsoft 365 — OAuth 2.0 + Microsoft Graph (delegated). */
export const fetchMicrosoftOAuthAuthorizeUrlAndRedirect = async (sectorId: number): Promise<void> => {
  const response = await axios.get<{ url: string }>(
    `${API_URL}/EmailConnection/oauth/microsoft/authorize-url?sectorId=${sectorId}`,
    { headers: getAuthHeaders() }
  );
  const url = response.data?.url;
  if (!url) throw new Error('No redirect URL received');
  window.location.href = url;
};

export interface SmtpConnectionTestResult {
  smtpOk: boolean;
  imapOk: boolean;
  smtpError: string | null;
  imapError: string | null;
}

/** Connect + AUTH on SMTP and IMAP (no email sent). */
export const testSmtpConnection = async (dto: {
  sectorId: number;
  host: string;
  port: number;
  username: string;
  password: string;
}): Promise<SmtpConnectionTestResult> => {
  const response = await axios.post<SmtpConnectionTestResult>(
    `${API_URL}/EmailConnection/smtp/test`,
    dto,
    { headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' }, timeout: 95000 }
  );
  return response.data;
};

export const saveSmtpConfig = async (dto: {
  sectorId: number;
  host: string;
  port: number;
  username: string;
  password: string;
}): Promise<void> => {
  await axios.post(
    `${API_URL}/EmailConnection/smtp`,
    dto,
    { headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' }, timeout: 95000 }
  );
};

/** SPF/DKIM/DMARC check via public DNS (same view as propagation worldwide). */
export interface MendsDkimGuidance {
  hasGeneratedKeys: boolean;
  selector: string | null;
  domain: string | null;
  dnsHostFqdn: string | null;
  dnsTxtValue: string | null;
  providerNote: string | null;
}

export interface RecommendedDnsRow {
  step: number;
  role: string;
  kind: string;
  host: string;
  value: string;
  note: string;
}

export interface EmailDeliverabilityCheck {
  accountEmail: string | null;
  domain: string | null;
  isConsumerMailbox: boolean;
  consumerMailboxNote: string | null;
  checkedAtUtc: string;
  alignmentGuide: string | null;
  mendsDkim: MendsDkimGuidance | null;
  spf: {
    found: boolean;
    spfTxtValues: string[];
    summary: string | null;
  };
  dmarc: {
    found: boolean;
    rawRecord: string | null;
    policy: string | null;
    summary: string | null;
  };
  dkim: {
    anyFound: boolean;
    selectorsChecked: Array<{ selector: string; found: boolean; summary: string | null }>;
  };
  recommendedRecords: RecommendedDnsRow[];
  overallHealthy: boolean;
  error: string | null;
}

export const getEmailDeliverabilityCheck = async (sectorId: number): Promise<EmailDeliverabilityCheck> => {
  const response = await axios.get<EmailDeliverabilityCheck>(
    `${API_URL}/EmailConnection/deliverability/${sectorId}`,
    { headers: getAuthHeaders() }
  );
  return response.data;
};

export const disconnectEmail = async (sectorId: number): Promise<void> => {
  await axios.post(
    `${API_URL}/EmailConnection/disconnect/${sectorId}`,
    {},
    { headers: getAuthHeaders() }
  );
};

export const triggerEmailSync = async (sectorId: number): Promise<void> => {
  await axios.post(
    `${API_URL}/EmailConnection/sync/${sectorId}`,
    {},
    { headers: getAuthHeaders() }
  );
};
